import { CartAddEvent, CartUpdateEvent } from '@theme/events';

let giftPending = false;

async function claimGift(button) {
  if (giftPending || button.disabled) return;
  const panel = button.closest('.ck-gift-choice');
  const select = panel?.querySelector('[data-cls-gift-size]');
  const status = panel?.querySelector('[data-cls-gift-status]');
  if (!select || !status) return;
  if (!select.value) {
    status.textContent = button.dataset.required;
    select.focus();
    return;
  }
  giftPending = true;
  const label = button.textContent;
  button.disabled = true;
  button.textContent = button.dataset.busy;
  button.classList.add('is-loading');
  status.textContent = '';
  let added = false;
  const claimId = String(Date.now()) + '-' + Math.random().toString(36).slice(2);
  const root = Theme.routes.cart_add_url.replace(/cart\/add(?:\.js)?$/, '');
  const readCart = async () => {
    const response = await fetch(root + 'cart.js', { cache: 'no-store' });
    if (!response.ok) throw new Error(button.dataset.error);
    return response.json();
  };
  const removeClaim = async () => {
    const cart = await readCart();
    const item = cart.items.find(item => item.properties?._cls_claim === claimId);
    if (!item) return;
    const response = await fetch(root + 'cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 }),
    });
    if (!response.ok) throw new Error(button.dataset.error);
  };
  try {
    const before = await readCart();
    if (before.items.some(item => item.properties?._cls_gift === 'classic-socks')) throw new Error(button.dataset.error);
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: select.value, quantity: 1, properties: { _cls_gift: 'classic-socks', _cls_claim: claimId } }],
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || button.dataset.error);
    const after = await readCart();
    const gift = after.items.find(item => item.properties?._cls_claim === claimId);
    if (!gift || gift.final_line_price !== 0 || after.total_price > before.total_price) {
      await removeClaim();
      status.textContent = button.dataset.check;
      return;
    }
    added = true;
    document.dispatchEvent(new CartAddEvent(result, 'cls-gift-cta', {
      source: 'cls-gift-cta',
      sections: result.sections,
    }));
  } catch (error) {
    try { await removeClaim(); } catch { status.textContent = button.dataset.error; return; }
    status.textContent = error.message || button.dataset.error;
  } finally {
    giftPending = false;
    if (!added) {
      button.disabled = false;
      button.textContent = label;
      button.classList.remove('is-loading');
    }
  }
}

document.addEventListener('click', event => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-cls-gift-claim]');
  if (!button) return;
  event.preventDefault();
  claimGift(button);
});


document.addEventListener('change', event => {
  if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-cls-gift-size]')) return;
  const status = event.target.closest('.ck-gift-choice')?.querySelector('[data-cls-gift-status]');
  if (status) status.textContent = '';
});



// A widget-claimed gift must never silently become a paid item after a cart edit.
let giftReview = null;
let giftReviewTimer;
let giftCheckoutApproved = false;
const giftGuardSource = 'cls-gift-price-guard';
const isClaimedGift = item => item.properties?._cls_gift === 'classic-socks'
  && typeof item.properties?._cls_claim === 'string' && item.properties._cls_claim.length > 0;

function giftNotice(message) {
  let note = document.getElementById('cls-gift-guard-notice');
  if (!note) {
    note = document.createElement('div');
    note.id = 'cls-gift-guard-notice';
    note.setAttribute('role', 'status');
    note.style.cssText = 'position:fixed;bottom:18px;left:18px;right:18px;z-index:99999;padding:16px 20px;background:#20241f;color:white;border-radius:12px;font-size:14px;box-shadow:0 5px 25px #0003;';
    (document.querySelector('dialog[open]') || document.body).appendChild(note);
  }
  note.textContent = message;
  setTimeout(() => note.remove(), 12000);
}

async function reviewClaimedGifts() {
  if (giftReview) return giftReview;
  if (giftPending) return false;
  giftReview = (async () => {
    const root = Theme.routes.cart_add_url.replace(/cart\/add(?:\.js)?$/, '');
    const response = await fetch(root + 'cart.js', { cache: 'no-store' });
    if (!response.ok) throw new Error('gift-cart-read');
    const cart = await response.json();
    const invalid = cart.items.filter(item => isClaimedGift(item) && item.final_line_price !== 0);
    if (!invalid.length) return false;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const updates = Object.fromEntries(invalid.map(item => [item.key, 0]));
    const changed = await fetch(root + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
    });
    if (!changed.ok) throw new Error('gift-cart-cleanup');
    const result = await changed.json();
    if (result.items.some(item => isClaimedGift(item) && item.final_line_price !== 0)) throw new Error('gift-cart-confirm');
    document.dispatchEvent(new CartUpdateEvent(result, giftGuardSource, {
      source: giftGuardSource, sections: result.sections, itemCount: result.item_count,
    }));
    giftNotice(document.documentElement.lang.startsWith('nl')
      ? 'Je cadeau is verwijderd omdat je winkelwagen niet meer aan de cadeauvoorwaarden voldoet. Zo betaal je nooit onverwacht voor het gratis paar.'
      : 'Your gift was removed because your cart no longer qualifies. You will not be unexpectedly charged for the free pair.');
    return true;
  })();
  try { return await giftReview; } finally { giftReview = null; }
}
function scheduleGiftReview(event) {
  if (event?.detail?.sourceId === giftGuardSource) return;
  clearTimeout(giftReviewTimer);
  giftReviewTimer = setTimeout(() => reviewClaimedGifts().catch(() => {
    giftNotice(document.documentElement.lang.startsWith('nl')
      ? 'Controleer je cadeau voor het afrekenen. De cadeauprijs kon niet worden gecontroleerd; probeer opnieuw.'
      : 'Please check your gift before checkout. Its price could not be verified; please try again.');
  }), 150);
}
document.addEventListener('cart:update', scheduleGiftReview);
document.addEventListener('discount:update', scheduleGiftReview);
window.addEventListener('pageshow', scheduleGiftReview);
scheduleGiftReview();

// Recheck before checkout as well, including edits immediately followed by checkout.
document.addEventListener('click', async event => {
  if (!(event.target instanceof Element)) return;
  const checkout = event.target.closest('button[name="checkout"], #checkout, a[href$="/checkout"]');
  if (!checkout || giftCheckoutApproved) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (giftPending) return;
  try {
    const removed = await reviewClaimedGifts();
    if (removed) return; // Let the customer review the corrected cart first.
    giftCheckoutApproved = true;
    try { checkout.click(); } finally { giftCheckoutApproved = false; }
  } catch {
    giftNotice(document.documentElement.lang.startsWith('nl')
      ? 'Afrekenen is even gepauzeerd: de cadeauprijs kon niet worden gecontroleerd. Probeer opnieuw.'
      : 'Checkout is paused because the gift price could not be verified. Please try again.');
  }
}, true);
